import { useEffect } from 'react';
import { RealtimeRole } from '@doxyme/realtime';
import {
  getApolloSessionDetails,
  getIsAudioCall,
} from '../../../features/apolloSession/apolloSession.selectors';
import {
  setDbSessionId,
  setStartCallTime,
} from '../../../features/apolloSession/apolloSession.slice';
import { useAppDispatch, useAppSelector } from '../../useReduxToolkit';
import * as realtime from '../../../features/realtime/realtime.slice';
import { useTrackCallStartEvent } from '../../useTracking';
import {
  CallInitStep,
  MediaSetupStep,
  SessionSetupStep,
  callInitTimer,
  mediaSetupTimer,
  sessionSetupTimer,
} from './callInitTimer';
import {
  type CustomDatadogContext,
  CustomDatadogContextKeys,
} from '../../../features/tracking/trackers/datadog/context.datadog';
import {
  setRumGlobalContextProps,
  setRumUser,
} from '../../../features/tracking/trackers/datadog/rum.datadog';
import { useLocalMediaActions } from '../../useLocalMedia';
import { getLogger, updateLoggerContext } from '../../../libs/logger';
import { RealtimeMessageType } from '../../../features/realtime/realtime.types';
import { CallTransition } from '../../../features/call/call.types';
import type { TransitionFunc } from '../useCall.types';
import { getIdentityId } from '../../../features/realtime/realtime.selectors';
import { useAudioCall } from '../../useAudioCall';
import { useListenForScreenshare } from '../../useScreenshareState/useListenForScreenshare';
import { resetPostCallSurvey } from '../../../features/postCallSurvey';
import { useGetTime } from '../../useGetTime';
import { usePatientCallHold } from '../../usePatientCallHold';
import { useGetUserId } from '../../useCredentials';
import { toast } from '@doxyme/ui/components/foundations';
import { Icon } from '@doxyme/icons';
import { useIntl } from '@doxyme/locale';
import { useService } from '../../useService';

const logger = getLogger('useCallStateInitializing');

export const useCallStateInitializing = (
  transition: TransitionFunc,
): (() => Promise<void>) => {
  // Bootstrap quality of service
  useService('qualityOfService');

  const getTime = useGetTime();
  /**
   * Using 'undefined' as a fallback to prevent test failures,
   * while ensuring logging remains consistent and does not break
   * due to an actual undefined value
   */
  const userId = useGetUserId() || 'undefined';
  const apolloSession = useAppSelector(getApolloSessionDetails);
  const identityId = useAppSelector(getIdentityId);
  useListenForScreenshare();
  const {
    isV2,
    sessionId,
    roomId,
    role = RealtimeRole.Patient,
    navigationStarted = 0,
  } = apolloSession;
  const dispatch = useAppDispatch();
  const mediaActions = useLocalMediaActions();
  const isProvider = role === RealtimeRole.Provider;
  const trackCallStartEvent = useTrackCallStartEvent();
  const isAudioCall = useAppSelector(getIsAudioCall);
  const { getMediaSettingsForAudioOnly } = useAudioCall();
  const { isCallOnHold } = usePatientCallHold();
  const { formatMessage } = useIntl();

  // Todo: add logger context when we have the fields available (call service plugin?)
  useEffect(() => {
    const sharedContextProps: CustomDatadogContext = {
      // TODO: Should go in app bootstrap
      [CustomDatadogContextKeys.Role]: role,
    };
    setRumGlobalContextProps(sharedContextProps);
    if (isProvider && userId) {
      // TODO: Should go in app bootstrap.
      setRumUser({ id: userId });
    }
    const loggerContextProps = { ...sharedContextProps, userId, sessionId };
    updateLoggerContext(loggerContextProps);
  }, [isProvider, role, sessionId]);

  const setupMedia = async (): Promise<void> => {
    mediaSetupTimer.start(MediaSetupStep.Start);
    let mediaSettings = undefined;
    if (isAudioCall) {
      // if audio call is started from V2 dashboard we need to stop media then start with audioOnly mediaSettings
      // mediaActions.restart doesn't work for OG dashboard
      await mediaActions.stop();
      mediaSettings = getMediaSettingsForAudioOnly();
    }
    await mediaActions.start(mediaSettings);
    mediaSetupTimer.finish(MediaSetupStep.MediaStarted);
  };

  const patientStart = async (): Promise<void> => {
    try {
      callInitTimer.start(CallInitStep.NavigationStarted, navigationStarted);
      callInitTimer.step(CallInitStep.NavigationFinished);

      await handlePatientJoin();
      callInitTimer.step(CallInitStep.SessionConnected);

      if (!isCallOnHold) {
        // TODO: this can be a plugin for call service
        trackCallStartEvent({
          sessionId,
          identityId,
          isAudioCall,
        });
      }
    } catch (error: unknown) {
      logger.error('useCall v2 start failed', {
        error,
        role,
      });
      throw error;
    }
  };

  const start = async (): Promise<void> => {
    void setupMedia();

    if (Boolean(isV2)) {
      await patientStart();
      return;
    }

    const time = getTime();
    dispatch(setStartCallTime(time));

    try {
      if (isProvider) {
        dispatch(resetPostCallSurvey());
      }

      const setupSession = async (): Promise<void> => {
        sessionSetupTimer.start(SessionSetupStep.Start);

        try {
          /**
           * Wait for provider to join Realtime.
           */
          sessionSetupTimer.step(SessionSetupStep.RealtimeConnected);
          if (identityId !== undefined && !isProvider) {
            await connectPatient();
          }
          sessionSetupTimer.finish(SessionSetupStep.SessionConnected);
        } catch (error) {
          logger.error(
            '[useCallStateInitializing/start] Join to realtime failed',
            {
              role,
              userId,
            },
            error,
          );
        }
      };

      await setupSession();

      callInitTimer.step(CallInitStep.SessionConnected);

      if (isProvider && roomId) {
        updateLoggerContext({ roomId });
      }
    } catch (error: unknown) {
      logger.error('useCall start failed', {
        error,
        role,
        userId,
      });
      throw error;
    }
  };

  // @TODO: Remove this in next 2 sprints. Related ticket: https://doxyme.atlassian.net/browse/PROD-4804
  const listenForSyncSessionData = async (): Promise<void> => {
    // The Patients dbSessionId should not be change during the call once set.
    let called = false;

    await dispatch(
      realtime.onMessage({
        messageType: RealtimeMessageType.ApolloSessionData,
        callback: (message) => {
          if (!called) {
            const dbSessionId = message.payload as string;
            dispatch(setDbSessionId(dbSessionId));
            called = true;
          }
        },
      }),
    ).unwrap();
  };

  async function connectPatient(): Promise<void> {
    await handlePatientJoin();

    const image = await mediaActions.getImageFromVideo({
      width: 100,
      height: 100,
    });
    if (image) {
      await dispatch(realtime.setPatientPicture(image));
    }
  }

  async function handlePatientJoin(): Promise<void> {
    await dispatch(
      realtime.onMessage({
        messageType: RealtimeMessageType.GroupCallParticipantLeft,
        callback: () => {
          void transition(CallTransition.GROUP_CALL_PARTICIPANT_REMOVED);
        },
      }),
    );

    await dispatch(
      realtime.onMessage({
        messageType: RealtimeMessageType.RemoveOnHoldParticipants,
        callback: () => {
          void transition(CallTransition.HANG_UP_ON_HOLD);
        },
      }),
    );

    await dispatch(
      realtime.onMessage({
        messageType: RealtimeMessageType.RemoveParticipantsAfterCallHangUp,
        callback: () => {
          void transition(CallTransition.HANG_UP);
        },
      }),
    );

    await dispatch(
      realtime.onMessage({
        messageType: RealtimeMessageType.ParticipantMuted,
        callback: (message) => {
          void mediaActions.toggleAudio(false);

          const username = message.payload as string;

          toast({
            title: formatMessage(
              { id: 'notification.providerMutedPatient' },
              { username },
            ),
            icon: <Icon.MicDisabled size='medium' />,
          });
        },
      }),
    );

    await dispatch(
      realtime.onMessage({
        messageType: RealtimeMessageType.CallRestart,
        callback: () => void transition(CallTransition.RESTART_INITIATED),
      }),
    );

    await listenForSyncSessionData();
  }

  // This should not happen, but we need to log it to investigate the issue with race condition on screenshare. Related ticket: https://doxyme.atlassian.net/browse/PROD-6505
  // TODO: remove this after we made investigation
  useEffect(() => {
    return () => {
      logger.info('unmounted');
    };
  }, []);

  return start;
};
